import React, { useContext, useState } from "react"
import { FormattedMessage } from "react-intl"
import Modal from "react-bootstrap/Modal"
import { FirebaseContext } from "../Firebase"
import { sendStandardToast } from "../toasts/standard-toast"
import SubmitButton from "../portfolio/SubmitButton"
import useUserEmailCredentials from "./useUserEmailCredentials"
import PasswordInput from "../forms/PasswordInput"

/**
 * Prompts a user to sign in again. Intended to be used after encountering a
 * auth/requires-recent-login error during sensitive operations
 * @param {boolean} show - whether the modal is open
 * @param {Function} onReauthSuccess - callback that runs after a successful reauthorization
 * @param {Function} handleClose - runs when modal is manually closed
 * @returns {JSX}
 */
const ReauthenticateModal = ({ show, handleClose, onReauthSuccess }) => {
  const { firebase } = useContext(FirebaseContext)
  const { email, reauthProvider } = useUserEmailCredentials()
  const [password, setPassword] = useState("")

  const reauthWithPassword = reauthProvider?.providerId === "password"

  const onClose = () => {
    setPassword("")
    handleClose()
  }

  const handleReauthorization = async () => {
    try {
      if (reauthWithPassword) {
        const passwordCredential = firebase.app.auth.EmailAuthProvider.credential(
          email,
          password,
        )
        await firebase.auth.currentUser.reauthenticateWithCredential(passwordCredential)
      } else {
        await firebase.auth.currentUser.reauthenticateWithPopup(reauthProvider)
      }
    } catch (e) {
      console.error("handleReauthorization error:", e)
      if (!["auth/popup-blocked", "auth/popup-closed-by-user", "auth/cancelled-popup-request"].includes(e.code)) {
        sendStandardToast({
          heading: "Unable to sign you in",
          message: e.code,
        })
      }
      return
    }
    await onReauthSuccess()
    onClose()
  }

  return (
    <Modal show={show} onHide={onClose} size="md">
      <Modal.Header closeButton>
        <div className="eyebrow component-eyebrow">
          <FormattedMessage id="account-page-please-confirm-its-you" defaultMessage="Please confirm it's you" />
        </div>
      </Modal.Header>
      <Modal.Body closeButton>
        <p>
          <FormattedMessage id="account-page-to-update-email" defaultMessage="To keep your account secure, we need you to sign in again." />
        </p>
        {reauthWithPassword && (
          <PasswordInput
            password={password}
            onPasswordChange={newPassword => setPassword(newPassword)}
            isNew={false}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <SubmitButton
          label={<FormattedMessage id="account-page-sign-in" defaultMessage="Sign In" />}
          onClick={handleReauthorization}
          className="account-page-submit-button"
        />
      </Modal.Footer>
    </Modal>
  )
}

export default ReauthenticateModal
